import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from 'react-router-dom';

import Alert from "../../components/form/Alert";
import CircularProgress from "../../components/form/CircularProgress";
import Empty from "../../assets/icons/Empty";
import Layout from "../../components/layout/Layout";
import MapTrack from "../../components/map/MapTrack";
import { Box, Stack, Typography } from "@mui/material";

import {
  cleanDetailRoute,
  cleanLastRoute,
  getByLastRoute,
  getIdOnRoutes,
  getAllOnRoutesByToken,
  GetDeliveryPointsByToken
} from "../../store/transactions/thunk/home";

const IsEmpty = ({ loading, empty }) => {
  return (
    <div className={`flex flex-col ${empty ? "flex-1" : "flex-0"}`}>
      {loading &&
        <Box sx={{ display: 'flex', height: "100%", width: '100%', justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </Box>
      }
      {(empty && !loading) &&
        <Box sx={{ display: 'flex', flexDirection: "column", height: "100%", width: '100%', justifyContent: "center", alignItems: "center" }}>
          <Empty />
          <Stack justifyContent="center" alignItems="center" spacing={1} pt={2}>
            <Typography variant="bml">Sin rutas en ruta</Typography>
            <Typography variant="caption">No hay rutas activas para este cliente</Typography>
          </Stack>
        </Box>
      }
    </div>
  )
}

const IsLoading = ({ loading }) => {
  return (
    <div className="absolute inset-0 flex justify-end items-start" style={{ marginTop: '10px', marginRight: '5%' }}>
      {loading && <CircularProgress className="relative z-50" />}
    </div>
  )
}

const SharedTracking = () => {
  const [__] = useTranslation("global");
  const dispatch = useDispatch();
  const { id } = useParams();

  const home = useSelector(state => state.transactions.home);
  const [alert, setAlert] = useState({ open: false, title: "", subtitle: "", type: "" })
  const [showLoading, setShowLoading] = useState(false)
  const [preserveViewport, setPreserveViewport] = useState(false)
  const [active, setActive] = useState(null)
  const [isChanging, setIsChanging] = useState(false)

  const closeAlert = () => {
    setAlert({ open: false, title: "", subtitle: "", type: "", btn: "" })
  }

  useEffect(() => {
    dispatch(getAllOnRoutesByToken(id))
  }, [dispatch, id])

  useEffect(() => {
    const interval = setInterval(() => {
      if (active && showLoading) {
        dispatch(getByLastRoute({ id: active.token }))
        setPreserveViewport(true)
      }
    }, 30000);
    return () => clearInterval(interval)
  }, [dispatch, showLoading, active])

  useEffect(() => {
    if (!active) return;
    const last = get(home, 'last', {});
    if (last.isSuccess && !last.data) {
      setAlert({
        open: true,
        title: "Sin tracking disponible",
        subtitle: "La ruta fue iniciada desde la web, no tiene tracking del conductor.",
        type: "error",
        btn: "Cerrar",
        func: closeAlert
      });
    }
  }, [get(home, 'last.isSuccess'), active])

  useEffect(() => {
    if (get(home, "detail.isSuccess") && get(home, "detail.data", null) == null) {
      setAlert({ open: true, title: "Ruta finalizada", subtitle: "La ruta ha sido finalizada correctamente.", type: "", btn: "" })
      setShowLoading(false)
    }
  }, [home.detail])

  const setNewActive = (route) => {
    dispatch(cleanDetailRoute())
    dispatch(cleanLastRoute())
    setActive(route)
    setIsChanging(true)
    setShowLoading(true)
    setPreserveViewport(false)
    dispatch(GetDeliveryPointsByToken({ id: route.token }))
    dispatch(getByLastRoute({ id: route.token }))
    setTimeout(() => setIsChanging(false), 50)
  }

  const routes = get(home, "routesByToken.data", []);
  const isLoadingList = get(home, "routesByToken.isLoading", false);

  return (
    <Layout propsToolbar={{ title: "Tracking" }} disableHead={true} disableLayout={true}>
      <div>
        <div className="absolute z-[1] h-full flex flex-row max-h-screen">
          <div id="main-menu" className='relative w-[300px] border-r-gray-400 border-r-[1px] min-h-screen h-full bg-white overflow-y-auto flex flex-col'>
            <div className='sticky top-0 z-[1] flex flex-row justify-between border-b-gray-400 border-b-[1px] p-[20px] bg-white'>
              <div className='uppercase'>Rutas en curso</div>
              <div>{routes.length}</div>
            </div>
            <IsEmpty loading={isLoadingList} empty={isEmpty(routes)} />
            <ul role="list" className={`divide-y divide-gray-400 border-b-gray-400 border-b-[1px] flex flex-col ${isEmpty(routes) ? "flex-0" : "flex-1"}`}>
              {map(routes, (route) => (
                <li
                  key={route.idRoute}
                  role="button"
                  onClick={() => setNewActive(route)}
                  className={`flex justify-between items-center gap-x-6 py-5 ${get(active, "idRoute") === route.idRoute ? "bg-gray-100" : "bg-[transparent]"} hover:bg-gray-100 active:bg-[#0000004a] p-[20px] duration-300 cursor-pointer`}
                >
                  <div className="flex min-w-0 w-full max-w-full">
                    <svg className='w-12 h-12 flex-none rounded-full bg-gray-50' viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect className={`${get(active, "idRoute") === route.idRoute ? "fill-[#B0E2FA]" : "fill-[#DFE7F3]"} h-[44px] w-[44px]`} rx="22" fill="#B0E2FA" />
                      <g clipPath="url(#clip0_824_89124)">
                        <path className={`${get(active, "idRoute") === route.idRoute ? "fill-[#0C709F]" : "fill-[#556C8F]"}`} d="M28.75 13.9395L14.07 20.0795C13.24 20.4295 13.26 21.6095 14.09 21.9295L19.43 23.9995C19.69 24.0995 19.9 24.3095 20 24.5695L22.06 29.8995C22.38 30.7395 23.57 30.7595 23.92 29.9295L30.07 15.2595C30.4 14.4295 29.57 13.5995 28.75 13.9395Z" />
                      </g>
                      <defs>
                        <clipPath id="clip0_824_89124">
                          <rect width="24" height="24" fill="white" transform="translate(10 10)" />
                        </clipPath>
                      </defs>
                    </svg>
                    <div className="min-w-0 flex-auto pl-[10px]">
                      <p className="text-sm font-semibold leading-6 text-gray-900">#:&nbsp;{route.idRoute}</p>
                      <p className="mt-1 truncate text-xs leading-5 text-gray-500">{route.routeName}</p>
                    </div>
                    <div className="flex items-center justify-end pl-[10px]">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-[15px] h-[15px] ${get(active, "idRoute") === route.idRoute ? "-rotate-180" : "rotate-0"} duration-500`}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className='h-screen w-full pl-[300px]'>
          <IsLoading loading={showLoading} />
          <MapTrack
            key={get(active, "idRoute", "empty")}
            driverPosition={active ? get(home, 'last.data', null) : null}
            locationSuggestions={active ? get(home, "trackingByToken.data.viewRouteDeliveryPoints", []) : []}
            locationsReal={[]}
            preserveViewport={preserveViewport}
          />
        </div>
      </div>

      <Alert
        title={get(alert, "title")}
        subtitle={get(alert, "subtitle")}
        btn1={{ func: closeAlert }}
        btn2={{}}
        type={get(alert, "type")}
        openAlert={get(alert, "open")}
        closeAlert={closeAlert}
        disableBackdrop
      />
    </Layout>
  )
}

export default SharedTracking
